import React, {memo, useState, useEffect, useCallback, useRef} from 'react';
import {usePopper} from 'react-popper';
import PropTypes from 'prop-types';
import {
	PopoverContext,
	defaultIgnoreSelectors,
	registerPopover,
	unregisterPopover,
	isClickInsideNestedPopover,
	isClickInsideParentPopover,
	findParentPopoverId,
} from './utils';

/**
 * Popover - Compound component for creating popover/dropdown menus with popper positioning
 *
 * @typedef {Object} PopoverProps
 * @property {React.ReactNode} children - PopoverTrigger and PopoverContent components
 * @property {boolean} [defaultOpen] - Initial open state
 * @property {boolean} [open] - Controlled open state
 * @property {function} [onOpenChange] - Callback when open state changes
 * @property {string} [placement] - Popper placement ('bottom', 'top', 'left', 'right', 'bottom-end', etc.)
 * @property {string} [className] - Additional CSS classes for container
 * @property {boolean} [closeOnOutsideClick] - Whether to close on outside click (default: true)
 * @property {boolean} [closeOnEscape] - Whether to close on Escape key (default: true)
 * @property {Array} [ignoreOutsideClickSelectors] - Selectors to ignore when clicking outside (e.g., '[data-multiselect-menu="true"]')
 * @property {Array<number>} [offset] - Custom offset [x, y] for popper positioning (default: [0, 5])
 */

const getFallbackPlacementsFor = placement => {
	if (typeof placement !== 'string' || placement.trim() === '') {
		return ['top-end', 'bottom-start', 'top-start'];
	}

	const [side, alignment] = placement.split('-');

	const uniq = arr => Array.from(new Set(arr.filter(Boolean)));

	// Note: In Popper, "top/bottom/left/right" are centered variants.
	// We keep fallbacks ordered by closest intent: opposite side first, then same side alt-align,
	// then opposite side alt-align, and finally perpendicular sides.
	if (side === 'top' || side === 'bottom') {
		const opposite = side === 'top' ? 'bottom' : 'top';
		// Centered variants should try to stay centered; only fall back to start/end as last resort.
		if (!alignment) {
			return uniq([
				opposite,
				side,
				// Perpendicular (still centered)
				'right',
				'left',
				// Last resort: allow edge-aligned variants if nothing fits.
				`${side}-start`,
				`${side}-end`,
				`${opposite}-start`,
				`${opposite}-end`,
			]);
		}

		return uniq([
			`${opposite}-${alignment}`,
			// Same side alternates
			placement,
			`${side}-${alignment === 'start' ? 'end' : 'start'}`,
			// Opposite side alternates
			opposite,
			`${opposite}-start`,
			`${opposite}-end`,
			// Perpendicular as last resort
			'right',
			'left',
		]);
	}

	if (side === 'left' || side === 'right') {
		const opposite = side === 'left' ? 'right' : 'left';
		// Centered variants should try to stay centered; only fall back to start/end as last resort.
		if (!alignment) {
			return uniq([
				opposite,
				side,
				// Perpendicular (still centered)
				'top',
				'bottom',
				// Last resort: allow edge-aligned variants if nothing fits.
				`${side}-start`,
				`${side}-end`,
				`${opposite}-start`,
				`${opposite}-end`,
			]);
		}

		return uniq([
			`${opposite}-${alignment}`,
			// Same side alternates
			placement,
			`${side}-${alignment === 'start' ? 'end' : 'start'}`,
			// Opposite side alternates
			opposite,
			`${opposite}-start`,
			`${opposite}-end`,
			// Perpendicular as last resort
			'top',
			'bottom',
		]);
	}

	// Unknown placement (or 'auto'): fall back to previous defaults.
	// Similar to: src/components/ui/Popover/Popover.js:64, Extract on 3rd occurrence
	return ['top-end', 'bottom-start', 'top-start'];
};

const Popover = memo(
	({
		children,
		defaultOpen = false,
		open: controlledOpen,
		onOpenChange,
		placement = 'bottom-end',
		className,
		closeOnOutsideClick = true,
		closeOnEscape = true,
		ignoreOutsideClickSelectors = defaultIgnoreSelectors,
		offset = [0, 5],
		...props
	}) => {
		const [internalOpen, setInternalOpen] = useState(defaultOpen);
		const [isClosing, setIsClosing] = useState(false);
		const [referenceElement, setReferenceElement] = useState(null);
		const [popperElement, setPopperElement] = useState(null);
		const popoverIdRef = useRef(`popover-${Math.random().toString(36).substr(2, 9)}`);

		const isControlled = controlledOpen !== undefined;
		const open = isControlled ? controlledOpen : internalOpen;

		const setOpen = useCallback(
			value => {
				if (!isControlled) {
					setInternalOpen(value);
				}
				onOpenChange?.(value);
			},
			[isControlled, onOpenChange],
		);

		const referenceRef = useCallback(node => setReferenceElement(node), []);
		const popperRef = useCallback(node => setPopperElement(node), []);

		const modifiers = React.useMemo(
			() => [
				{
					name: 'flip',
					options: {
						fallbackPlacements: getFallbackPlacementsFor(placement),
					},
				},
				{
					name: 'preventOverflow',
					options: {padding: 5, boundary: 'viewport'},
				},
				{
					name: 'offset',
					options: {offset},
				},
			],
			[offset, placement],
		);

		const {styles, attributes, update} = usePopper(referenceElement, popperElement, {
			placement,
			modifiers,
		});

		// Handle opening the popover - update popper position
		// Wait for both elements to be ready before updating
		useEffect(() => {
			if (
				open &&
				!isClosing &&
				referenceElement &&
				popperElement &&
				typeof update === 'function'
			) {
				setTimeout(() => update(), 0);
			}
		}, [open, isClosing, referenceElement, popperElement, update]);

		// Register/unregister popover for nested detection
		useEffect(() => {
			if (open && popperElement) {
				// Find parent popover by checking if reference element is inside another popover
				const parentId = findParentPopoverId(referenceElement);
				registerPopover(popoverIdRef.current, popperElement, referenceElement, parentId);
				return () => {
					unregisterPopover(popoverIdRef.current);
				};
			} else {
				unregisterPopover(popoverIdRef.current);
			}
		}, [open, popperElement, referenceElement]);

		// Handle click outside to close menu
		useEffect(() => {
			if (!open || !closeOnOutsideClick) return;

			const handleClickOutside = event => {
				const clickTarget = event.target;

				// Check if click is inside this popover's elements first
				const isInsideThisPopover =
					(popperElement && popperElement.contains(clickTarget)) ||
					(referenceElement && referenceElement.contains(clickTarget));

				if (isInsideThisPopover) {
					// Check if click is inside a nested child popover
					// If so, don't close this one - the nested one should handle it
					if (isClickInsideNestedPopover(clickTarget, popoverIdRef.current)) {
						return; // Click is inside a nested child, let it handle closing
					}
					// Click is inside this popover but not in a nested child - don't close
					return;
				}

				// Click is outside this popover
				// Check if click is inside a parent popover - if so, close this nested one
				// We do this BEFORE checking ignore selectors so we can close nested popovers
				// even when clicking inside parent popovers that match ignore selectors
				const isInsideParent = isClickInsideParentPopover(
					clickTarget,
					popoverIdRef.current,
				);

				if (isInsideParent) {
					// Click is in parent popover - close this nested one
					setIsClosing(true);
					setOpen(false);
					setTimeout(() => setIsClosing(false), 150);
					return;
				}

				// Ignore clicks inside ignored selectors (e.g., MultiSelect dropdowns)
				// But only if we're not inside a parent popover (already handled above)
				if (ignoreOutsideClickSelectors.length > 0 && clickTarget?.closest) {
					for (const selector of ignoreOutsideClickSelectors) {
						if (clickTarget.closest(selector)) {
							return;
						}
					}
				}

				// Click is truly outside both - close this popover
				setIsClosing(true);
				setOpen(false);
				setTimeout(() => setIsClosing(false), 150);
			};

			document.addEventListener('mousedown', handleClickOutside, true);
			document.addEventListener('touchstart', handleClickOutside, true);
			return () => {
				document.removeEventListener('mousedown', handleClickOutside, true);
				document.removeEventListener('touchstart', handleClickOutside, true);
			};
		}, [
			open,
			closeOnOutsideClick,
			popperElement,
			referenceElement,
			ignoreOutsideClickSelectors,
			setOpen,
		]);

		// Handle escape key to close menu
		useEffect(() => {
			if (!open || !closeOnEscape) return;

			const handleEscape = event => {
				if (event.key === 'Escape') {
					setIsClosing(true);
					setOpen(false);
					setTimeout(() => setIsClosing(false), 150);
				}
			};

			document.addEventListener('keydown', handleEscape);
			return () => {
				document.removeEventListener('keydown', handleEscape);
			};
		}, [open, closeOnEscape, setOpen]);

		const handleToggle = useCallback(() => {
			if (isClosing) return;
			setOpen(!open);
		}, [isClosing, open, setOpen]);

		const contextValue = {
			open,
			setOpen,
			isClosing,
			setIsClosing,
			referenceRef,
			popperRef,
			styles,
			attributes,
			handleToggle,
			update,
		};

		return (
			<PopoverContext.Provider value={contextValue}>
				<div className={className} {...props}>
					{children}
				</div>
			</PopoverContext.Provider>
		);
	},
);

Popover.propTypes = {
	children: PropTypes.node.isRequired,
	defaultOpen: PropTypes.bool,
	open: PropTypes.bool,
	onOpenChange: PropTypes.func,
	placement: PropTypes.string,
	className: PropTypes.string,
	closeOnOutsideClick: PropTypes.bool,
	closeOnEscape: PropTypes.bool,
	ignoreOutsideClickSelectors: PropTypes.arrayOf(PropTypes.string),
	offset: PropTypes.arrayOf(PropTypes.number),
};

Popover.displayName = 'Popover';

export default Popover;
